import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  salesPlanService, salesPlanError, type SalesPlanItem, type SaveSalesPlan, type SalesPlanBreed,
} from '../../services/sales-plan.service';
import './AdminSalesPlans.css';

const emptyItem = (): SalesPlanItem => ({
  region: '', sexAgeGroup: '', headCount: null, birthYear: '', category: '',
  averageWeight: '', averagePrice: '', askingPrice: '', notes: '',
});
const formatDate = (value: string) => value.split('-').reverse().join('.');
const fields: { key: keyof SalesPlanItem; label: string; hint?: string; maxLength?: number }[] = [
  { key: 'region', label: 'Регион', hint: 'Акмолинская область / По Казахстану', maxLength: 200 },
  { key: 'sexAgeGroup', label: 'Половозрастная группа *', hint: 'Телки 12–24 мес.', maxLength: 200 },
  { key: 'headCount', label: 'Количество голов' },
  { key: 'birthYear', label: 'Год рождения', hint: '2025 или 2024–2025', maxLength: 100 },
  { key: 'category', label: 'Категория', hint: '1 или 2', maxLength: 100 },
  { key: 'averageWeight', label: 'Средний вес, кг', hint: '350–450 / от 350 / 500+', maxLength: 200 },
  { key: 'averagePrice', label: 'Средняя цена', hint: '900 тыс. тг/гол. / от 4000 тг/кг / договорная', maxLength: 500 },
  { key: 'askingPrice', label: 'Запрашиваемая цена', hint: 'Сумма, единица цены и месяц, если нужен', maxLength: 500 },
  { key: 'notes', label: 'Примечание к строке', maxLength: 2000 },
];

function PlanEditor({ initial, id, breeds, onClose, onSaved }: {
  initial: SaveSalesPlan; id: number | null; breeds: SalesPlanBreed[];
  onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const changeItem = (index: number, field: keyof SalesPlanItem, value: string) => {
    setForm(current => ({ ...current, items: current.items.map((item, i) => i !== index ? item : {
      ...item, [field]: field === 'headCount' ? (value === '' ? null : Number(value)) : value,
    }) }));
  };
  const moveItem = (index: number, offset: number) => setForm(current => {
    const items = [...current.items];
    [items[index], items[index + offset]] = [items[index + offset], items[index]];
    return { ...current, items };
  });
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (form.periodTo < form.periodFrom) { setError('Окончание периода не может быть раньше начала.'); return; }
    if (form.items.some(item => !item.sexAgeGroup.trim())) { setError('Заполните половозрастную группу каждой строки.'); return; }
    setSaving(true);
    try {
      const result = id === null ? await salesPlanService.create(form) : await salesPlanService.update(id, form);
      if (!result.success) throw new Error(result.message || 'Не удалось сохранить план.');
      onSaved();
    } catch (e) { setError(salesPlanError(e)); }
    finally { setSaving(false); }
  };
  return (
    <form className="sales-editor" onSubmit={save}>
      <h2>{id === null ? 'Новый план продаж' : 'Редактирование плана продаж'}</h2>
      <p className="sales-hint">Цены указывайте с единицей: тг/гол. или тг/кг живого веса. Неизвестные значения оставляйте пустыми.</p>
      {error && <p role="alert" className="sales-error">{error}</p>}
      <fieldset disabled={saving}>
        <div className="sales-fields">
          <label>Порода КРС *
            <select required value={form.breedId || ''} onChange={e => setForm({ ...form, breedId: Number(e.target.value) })}>
              <option value="">Выберите породу</option>
              {breeds.map(breed => <option key={breed.id} value={breed.id}>{breed.nameRu}</option>)}
            </select>
          </label>
          <label>Начало периода *<input type="date" required value={form.periodFrom} onChange={e => setForm({ ...form, periodFrom: e.target.value })} /></label>
          <label>Окончание периода *<input type="date" required min={form.periodFrom} value={form.periodTo} onChange={e => setForm({ ...form, periodTo: e.target.value })} /></label>
        </div>
        <label>Примечание к плану<textarea maxLength={4000} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
        <label className="sales-publish"><input type="checkbox" checked={form.isPublished} onChange={e => setForm({ ...form, isPublished: e.target.checked })} />Опубликовать в мобильном приложении</label>
        {form.items.map((item, index) => (
          <section className="sales-item-form" key={index} aria-label={`Строка ${index + 1}`}>
            <div className="sales-row-header">
              <h3>Строка {index + 1}</h3>
              <div className="sales-actions">
                <button type="button" disabled={index === 0} onClick={() => moveItem(index, -1)} aria-label={`Поднять строку ${index + 1}`}>Выше</button>
                <button type="button" disabled={index === form.items.length - 1} onClick={() => moveItem(index, 1)} aria-label={`Опустить строку ${index + 1}`}>Ниже</button>
                <button type="button" disabled={form.items.length === 1} onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}>Удалить строку</button>
              </div>
            </div>
            <div className="sales-fields">
              {fields.map(field => <label key={field.key}>{field.label}
                <input
                  type={field.key === 'headCount' ? 'number' : 'text'}
                  min={field.key === 'headCount' ? 0 : undefined} max={field.key === 'headCount' ? 100000000 : undefined}
                  step={field.key === 'headCount' ? 1 : undefined} maxLength={field.maxLength}
                  required={field.key === 'sexAgeGroup'} placeholder={field.hint}
                  value={item[field.key] ?? ''} onChange={e => changeItem(index, field.key, e.target.value)}
                />
              </label>)}
            </div>
          </section>
        ))}
        <button type="button" disabled={form.items.length >= 200} onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>Добавить строку</button>
        <div className="sales-actions sales-save">
          <button className="sales-primary" type="submit">{saving ? 'Сохранение…' : 'Сохранить план'}</button>
          <button type="button" onClick={onClose}>Отмена</button>
        </div>
      </fieldset>
    </form>
  );
}

export default function AdminSalesPlans() {
  const queryClient = useQueryClient();
  const [breedId, setBreedId] = useState('');
  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('');
  const [editor, setEditor] = useState<{ id: number | null; form: SaveSalesPlan } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const breeds = useQuery({ queryKey: ['sales-plan-breeds'], queryFn: async () => {
    const result = await salesPlanService.breeds();
    if (!result.success || !result.data) throw new Error(result.message || 'Не удалось загрузить породы.');
    return result.data;
  } });
  const plans = useQuery({ queryKey: ['admin-sales-plans', breedId], queryFn: async () => {
    const result = await salesPlanService.list(breedId ? Number(breedId) : undefined);
    if (!result.success || !result.data) throw new Error(result.message || 'Не удалось загрузить планы.');
    return result.data;
  } });
  const openNew = () => {
    setMessage(''); setError('');
    setEditor({ id: null, form: { breedId: Number(breedId) || 0, periodFrom: '', periodTo: '', notes: '', isPublished: false, items: [emptyItem()] } });
  };
  const openEdit = async (id: number) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await salesPlanService.get(id);
      if (!result.success || !result.data) throw new Error(result.message || 'Не удалось открыть план.');
      const plan = result.data;
      setEditor({ id, form: { breedId: plan.breed.id, periodFrom: plan.periodFrom, periodTo: plan.periodTo, notes: plan.notes, isPublished: plan.isPublished, items: plan.items } });
    } catch (e) { setError(salesPlanError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id: number) => {
    if (!window.confirm('Удалить план продаж со всеми строками? Он исчезнет и из мобильного приложения.')) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await salesPlanService.delete(id);
      if (!result.success) throw new Error(result.message || 'Не удалось удалить план.');
      await queryClient.invalidateQueries({ queryKey: ['admin-sales-plans'] });
      setMessage('План удалён.');
    } catch (e) { setError(salesPlanError(e)); }
    finally { setBusy(false); }
  };
  const filtered = (plans.data || []).filter(plan =>
    (status === 'all' || plan.isPublished === (status === 'published')) &&
    (!month || (plan.periodFrom.slice(0, 7) <= month && plan.periodTo.slice(0, 7) >= month)));

  return (
    <div className="admin-sales-plans">
      <div className="sales-heading"><div><h1>План продаж</h1><p>Племенной КРС: объёмы и цены по породам, регионам и периодам.</p></div>
        {!editor && <button className="sales-primary" disabled={busy || !breeds.data?.length} onClick={openNew}>Добавить план</button>}
      </div>
      {message && <p role="status" className="sales-success">{message}</p>}
      {error && <p role="alert" className="sales-error">{error}</p>}
      {breeds.isError && <p role="alert" className="sales-error">Ошибка загрузки пород. <button onClick={() => breeds.refetch()}>Повторить</button></p>}
      {breeds.data?.length === 0 && <p>В справочнике нет пород КРС. Сначала добавьте их в справочник пород.</p>}
      {editor ? <PlanEditor key={editor.id ?? 'new'} initial={editor.form} id={editor.id} breeds={breeds.data || []}
        onClose={() => setEditor(null)} onSaved={() => {
          setEditor(null); setMessage('План сохранён.');
          void queryClient.invalidateQueries({ queryKey: ['admin-sales-plans'] });
        }} /> : <>
        <div className="sales-filters">
          <label>Порода<select value={breedId} onChange={e => setBreedId(e.target.value)}><option value="">Все породы</option>{breeds.data?.map(b => <option key={b.id} value={b.id}>{b.nameRu}</option>)}</select></label>
          <label>Месяц<input type="month" value={month} onChange={e => setMonth(e.target.value)} /></label>
          <label>Публикация<select value={status} onChange={e => setStatus(e.target.value)}><option value="all">Все</option><option value="published">Опубликованы</option><option value="draft">Черновики</option></select></label>
        </div>
        {plans.isPending ? <p role="status">Загрузка…</p> : plans.isError ? <p role="alert" className="sales-error">Не удалось загрузить планы. <button onClick={() => plans.refetch()}>Повторить</button></p> :
          filtered.length === 0 ? <p className="sales-empty">Планов пока нет. Добавьте план или измените фильтры.</p> :
            <div className="sales-table-wrap"><table><thead><tr><th>Порода</th><th>Период</th><th>Строк</th><th>Статус</th><th>Действия</th></tr></thead><tbody>
              {filtered.map(plan => <tr key={plan.id}>
                <td>{plan.breed.nameRu}</td><td>{formatDate(plan.periodFrom)} — {formatDate(plan.periodTo)}</td><td>{plan.itemsCount}</td>
                <td><span className={`sales-badge ${plan.isPublished ? 'published' : ''}`}>{plan.isPublished ? 'Опубликован' : 'Черновик'}</span></td>
                <td><div className="sales-actions"><button disabled={busy} onClick={() => openEdit(plan.id)}>Редактировать</button><button disabled={busy} onClick={() => remove(plan.id)}>Удалить</button></div></td>
              </tr>)}
            </tbody></table></div>}
      </>}
    </div>
  );
}
